import { Step } from 'react-joyride';

import { AppDispatch, RootState } from 'app/store';

import { selectGuideCompletedPageTours } from 'features/auth/authSelectors';
import authService from 'features/auth/authService';
import { updateUser } from 'features/auth/authSlice';
import {
  setNextStepIndex as setNextStepIndexAction,
  setStepIndex as setStepIndexAction,
  startTour as startTourAction,
  stopTour as stopTourAction
} from 'features/tour/tourSlice';

import { GuideCompleted, TourState, TourStep } from 'types/tour';

import { GuideType } from 'constants/enums';

import { getGuidePagesSettings } from './getGuidePagesSettings';
import { TourNavigator } from './tourNavigator';

type GetTourState = () => TourState;
type GetRootState = () => RootState;

interface TourEngineOptions {
  getState: GetTourState;
  getRootState: GetRootState;
  dispatch: AppDispatch;
  navigator: TourNavigator;
  onCloseSidebar?: () => void;
  onError?: (error: unknown) => void;
  onDismissGuide?: (guideCompleted: GuideCompleted) => Promise<void>;
}

export class TourEngine {
  private getState: GetTourState;

  private getRootState: GetRootState;

  private dispatch: AppDispatch;

  private navigator: TourNavigator;

  private onCloseSidebar?: () => void;

  private onError?: (error: unknown) => void;

  private onDismissGuide?: (guideCompleted: GuideCompleted) => Promise<void>;

  constructor({
    getState,
    getRootState,
    dispatch,
    navigator,
    onCloseSidebar,
    onError,
    onDismissGuide
  }: TourEngineOptions) {
    this.getState = getState;
    this.getRootState = getRootState;
    this.dispatch = dispatch;
    this.navigator = navigator;
    this.onCloseSidebar = onCloseSidebar;
    this.onError = onError;
    this.onDismissGuide = onDismissGuide;
  }

  private handleSidebarBeforeStepChange(
    currentStep: TourStep | undefined,
    nextStep: TourStep | undefined
  ) {
    const currentPage = currentStep?.page;
    const nextPage = nextStep?.page;

    if (currentPage && nextPage && currentPage !== nextPage) {
      this.onCloseSidebar?.();
    }
  }

  private handleGuideCompletion(
    guideType: GuideType,
    guideCompleted: GuideCompleted
  ): void {
    const { guideStep, userUpdate } = getGuidePagesSettings(
      guideType,
      guideCompleted
    );

    authService
      .readGuideSteps(guideStep)
      .then(() => {
        this.dispatch(updateUser(userUpdate));
      })
      .catch((error) => {
        this.onError?.(error);
      });
  }

  start(steps: TourStep[], startPage?: string, isFullGuide: boolean = false) {
    this.dispatch(startTourAction({ steps, startPage, isFullGuide }));
  }

  stop() {
    this.dispatch(stopTourAction());
  }

  async beforeStep(stepIndex: number, joyrideStep?: Step) {
    const state = this.getState();
    const currentStep = state.steps[stepIndex];

    if (!currentStep) {
      return;
    }

    if (currentStep.page && currentStep.page !== state.currentPage) {
      this.dispatch(setNextStepIndexAction(stepIndex));
      await this.navigator.goToPage(currentStep.page, stepIndex);
      return;
    }

    const targetSelector =
      (joyrideStep?.target as string) || (currentStep.target as string);

    await this.navigator.scrollToTarget(targetSelector);
  }

  async next() {
    const state = this.getState();
    const rootState = this.getRootState();
    const { stepIndex, steps } = state;

    const guideCompletedPageTours = selectGuideCompletedPageTours(rootState);
    const currentStep = steps[stepIndex];
    const nextIndex = stepIndex + 1;
    const nextStep = steps[nextIndex];

    const isLastStep = stepIndex >= steps.length - 1;
    const isFullGuide = state.isFullGuide === true;
    const isLastJobStep = currentStep?.isFinishJobsTour === true;
    const isLastApplicationsStep =
      currentStep?.isFinishApplicationsTour === true;
    const isLastFullGuideStep = isLastStep && isFullGuide;

    if (currentStep?.isFinishJobsTour && !isLastFullGuideStep) {
      this.handleGuideCompletion(GuideType.Jobs, guideCompletedPageTours);
    }

    if (isLastApplicationsStep && !isLastFullGuideStep) {
      this.handleGuideCompletion(
        GuideType.Applications,
        guideCompletedPageTours
      );
    }

    if (isLastStep) {
      if (isLastFullGuideStep && this.onDismissGuide) {
        const guideCompletedWithDismiss = {
          ...guideCompletedPageTours,
          dismissGuide: true
        };

        if (isLastJobStep) {
          guideCompletedWithDismiss.jobsGuideCompleted = true;
        }

        if (isLastApplicationsStep) {
          guideCompletedWithDismiss.applicationsGuideCompleted = true;
        }

        await this.onDismissGuide(guideCompletedWithDismiss);
      }
      await this.finish();
      return;
    }

    this.handleSidebarBeforeStepChange(currentStep, nextStep);

    if (currentStep?.nextAction === 'navigate' && currentStep.targetPage) {
      this.dispatch(setNextStepIndexAction(nextIndex));
      await this.navigator.goToPage(currentStep.targetPage, nextIndex);
      return;
    }

    this.dispatch(setStepIndexAction(nextIndex));
  }

  async prev() {
    const state = this.getState();
    const { stepIndex, steps } = state;

    if (stepIndex <= 0) {
      return;
    }

    const currentStep = steps[stepIndex];
    const previousIndex = stepIndex - 1;
    const previousStep = steps[previousIndex];

    this.handleSidebarBeforeStepChange(currentStep, previousStep);

    if (previousStep?.page && previousStep.page !== state.currentPage) {
      this.dispatch(setNextStepIndexAction(previousIndex));
      await this.navigator.goToPage(previousStep.page, previousIndex);
      return;
    }

    this.dispatch(setStepIndexAction(previousIndex));
  }

  goto(index: number) {
    this.dispatch(setStepIndexAction(index));
  }

  async navigate(page: string, stepIndex: number) {
    await this.navigator.goToPage(page, stepIndex);
  }

  private async finish() {
    this.stop();
  }
}
